import { useState, useEffect } from 'react';

const API = '/api';

const Icon = ({ d, size = 20, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
        stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
        {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
    </svg>
);

const icons = {
    lock: ['M19 11H5a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7a2 2 0 0 0-2-2z', 'M7 11V7a5 5 0 0 1 10 0v4'],
    eye: ['M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z', 'M12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6z'],
    eyeOff: ['M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94', 'M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19', 'M1 1l22 22'],
    building: ['M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z', 'M9 22V12h6v10'],
    arrow: 'M5 12h14M12 5l7 7-7 7',
    check: 'M20 6L9 17l-5-5',
    alert: ['M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z', 'M12 9v4', 'M12 17h.01'],
};

export default function ClientResetPassword({ onGoLogin }) {
    const [token, setToken] = useState('');
    const [form, setForm] = useState({ password: '', confirm: '' });
    const [showPass, setShowPass] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [tokenValid, setTokenValid] = useState(true);

    useEffect(() => {
        // Extract token from hash query string: #client-reset-password?token=xxx
        const hash = window.location.hash;
        const qIndex = hash.indexOf('?');
        if (qIndex !== -1) {
            const params = new URLSearchParams(hash.substring(qIndex + 1));
            const t = params.get('token');
            if (t) {
                setToken(t);
            } else {
                setTokenValid(false);
                setError('No reset token found in the link. Please request a new password reset.');
            }
        } else {
            setTokenValid(false);
            setError('Invalid reset link. Please request a new password reset.');
        }
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (form.password.length < 6) {
            setError('Password must be at least 6 characters long.');
            return;
        }
        if (form.password !== form.confirm) {
            setError('Passwords do not match. Please re-enter.');
            return;
        }

        setLoading(true);
        try {
            const res = await fetch(`${API}/client-reset-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token, password: form.password }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || 'Failed to reset password');
            setSuccess(true);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const goToLogin = () => {
        window.location.hash = 'client-login';
        if (onGoLogin) onGoLogin();
    };

    return (
        <div style={styles.page}>
            <div style={styles.bgBlob1} />
            <div style={styles.bgBlob2} />

            <div style={styles.card}>
                {/* Brand Header */}
                <div style={styles.brandHeader}>
                    <div style={styles.logoRing}>
                        <div style={styles.logoInner}>
                            <Icon d={icons.building} size={28} color="white" />
                        </div>
                    </div>
                    <div style={styles.brandBadge}>CLIENT PORTAL</div>
                </div>

                {/* Success State */}
                {success ? (
                    <div style={{ textAlign: 'center', padding: '8px 0 12px' }}>
                        <div style={{
                            width: 64, height: 64, borderRadius: '50%',
                            background: 'linear-gradient(135deg, #d1fae5, #a7f3d0)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            margin: '0 auto 20px',
                            boxShadow: '0 8px 24px rgba(16,185,129,0.2)'
                        }}>
                            <Icon d={icons.check} size={28} color="#059669" />
                        </div>
                        <h2 style={{ fontSize: 22, fontWeight: 800, color: '#063A5C', margin: '0 0 12px' }}>
                            Password Reset!
                        </h2>
                        <p style={{ fontSize: 14, color: '#64748b', lineHeight: 1.7, margin: '0 0 28px' }}>
                            Your password has been updated successfully.<br />
                            You can now sign in with your new password.
                        </p>
                        <button onClick={goToLogin} style={styles.submitBtn}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}>
                                Go to Login <Icon d={icons.arrow} size={18} color="white" />
                            </span>
                        </button>
                    </div>
                ) : !tokenValid ? (
                    /* Invalid Token State */
                    <div style={{ textAlign: 'center', padding: '8px 0 12px' }}>
                        <div style={{
                            width: 64, height: 64, borderRadius: '50%',
                            background: '#fef2f2',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            margin: '0 auto 20px',
                            border: '2px solid #fecaca'
                        }}>
                            <Icon d={icons.alert} size={28} color="#ef4444" />
                        </div>
                        <h2 style={{ fontSize: 20, fontWeight: 800, color: '#063A5C', margin: '0 0 12px' }}>
                            Invalid Reset Link
                        </h2>
                        <p style={{ fontSize: 14, color: '#64748b', lineHeight: 1.7, margin: '0 0 24px' }}>
                            {error || 'This reset link is invalid or has expired.'}<br />
                            Please request a new password reset from the login page.
                        </p>
                        <button onClick={goToLogin} style={styles.submitBtn}>
                            Back to Login
                        </button>
                    </div>
                ) : (
                    /* Reset Password Form */
                    <>
                        <h2 style={{ fontSize: 22, fontWeight: 800, color: '#063A5C', textAlign: 'center', margin: '0 0 8px' }}>
                            Set New Password
                        </h2>
                        <p style={{ fontSize: 13, color: '#64748b', textAlign: 'center', margin: '0 0 28px', lineHeight: 1.6 }}>
                            Create a strong new password for your account.
                        </p>

                        <form onSubmit={handleSubmit} style={styles.form}>
                            {error && (
                                <div style={styles.errorBox}>
                                    <span style={{ fontSize: 16 }}>⚠️</span>
                                    <span>{error}</span>
                                </div>
                            )}

                            {/* New Password */}
                            <div style={styles.fieldGroup}>
                                <label style={styles.label}>New Password</label>
                                <div style={styles.inputWrap}>
                                    <span style={styles.inputIcon}>
                                        <Icon d={icons.lock} size={17} color="#00B2A9" />
                                    </span>
                                    <input
                                        id="new-password"
                                        style={{ ...styles.input, paddingRight: 48 }}
                                        type={showPass ? 'text' : 'password'}
                                        placeholder="Min. 6 characters"
                                        required
                                        minLength={6}
                                        value={form.password}
                                        onChange={e => setForm({ ...form, password: e.target.value })}
                                        autoComplete="new-password"
                                    />
                                    <button type="button" onClick={() => setShowPass(s => !s)} style={styles.eyeBtn}>
                                        <Icon d={showPass ? icons.eyeOff : icons.eye} size={16} color="#94a3b8" />
                                    </button>
                                </div>
                                {/* Password strength indicator */}
                                {form.password && (
                                    <div style={{ display: 'flex', gap: 4, marginTop: 6 }}>
                                        {[1, 2, 3, 4].map(i => {
                                            const strength = form.password.length >= 12 ? 4
                                                : form.password.length >= 8 ? 3
                                                : form.password.length >= 6 ? 2 : 1;
                                            const colors = { 1: '#ef4444', 2: '#f97316', 3: '#eab308', 4: '#22c55e' };
                                            return (
                                                <div key={i} style={{
                                                    flex: 1, height: 4, borderRadius: 4,
                                                    background: i <= strength ? colors[strength] : '#e2e8f0',
                                                    transition: 'background 0.3s'
                                                }} />
                                            );
                                        })}
                                        <span style={{ fontSize: 11, color: '#64748b', marginLeft: 4 }}>
                                            {form.password.length >= 12 ? 'Strong' : form.password.length >= 8 ? 'Good' : form.password.length >= 6 ? 'Fair' : 'Weak'}
                                        </span>
                                    </div>
                                )}
                            </div>

                            {/* Confirm Password */}
                            <div style={styles.fieldGroup}>
                                <label style={styles.label}>Confirm Password</label>
                                <div style={styles.inputWrap}>
                                    <span style={styles.inputIcon}>
                                        <Icon d={icons.lock} size={17} color="#00B2A9" />
                                    </span>
                                    <input
                                        id="confirm-password"
                                        style={{
                                            ...styles.input, paddingRight: 48,
                                            borderColor: form.confirm && form.confirm !== form.password ? '#fca5a5' : undefined,
                                        }}
                                        type={showConfirm ? 'text' : 'password'}
                                        placeholder="Re-enter your new password"
                                        required
                                        value={form.confirm}
                                        onChange={e => setForm({ ...form, confirm: e.target.value })}
                                        autoComplete="new-password"
                                    />
                                    <button type="button" onClick={() => setShowConfirm(s => !s)} style={styles.eyeBtn}>
                                        <Icon d={showConfirm ? icons.eyeOff : icons.eye} size={16} color="#94a3b8" />
                                    </button>
                                </div>
                                {form.confirm && form.confirm !== form.password && (
                                    <span style={{ fontSize: 11, color: '#ef4444', marginTop: 4 }}>Passwords do not match</span>
                                )}
                                {form.confirm && form.confirm === form.password && (
                                    <span style={{ fontSize: 11, color: '#22c55e', marginTop: 4 }}>✓ Passwords match</span>
                                )}
                            </div>

                            <button
                                id="reset-password-btn"
                                type="submit"
                                disabled={loading}
                                style={{ ...styles.submitBtn, opacity: loading ? 0.7 : 1, marginTop: 8 }}
                            >
                                {loading ? (
                                    <span style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}>
                                        <span style={styles.spinner} /> Updating Password…
                                    </span>
                                ) : (
                                    <span style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}>
                                        Set New Password <Icon d={icons.check} size={18} color="white" />
                                    </span>
                                )}
                            </button>
                        </form>

                        <div style={{ textAlign: 'center', marginTop: 20 }}>
                            <button
                                onClick={goToLogin}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 13 }}
                            >
                                Remember your password? <span style={{ color: '#00B2A9', fontWeight: 700 }}>Sign In</span>
                            </button>
                        </div>
                    </>
                )}
            </div>

            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
    );
}

const styles = {
    page: {
        minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'linear-gradient(135deg, #eef2ff 0%, #f0f7ff 40%, #e0f0ff 100%)',
        fontFamily: "'Inter', 'Segoe UI', sans-serif",
        position: 'relative', overflow: 'hidden', padding: 20,
    },
    bgBlob1: {
        position: 'absolute', top: -120, right: -120, width: 500, height: 500, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(99,102,241,0.15) 0%, transparent 70%)',
        pointerEvents: 'none',
    },
    bgBlob2: {
        position: 'absolute', bottom: -100, left: -100, width: 400, height: 400, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(139,92,246,0.12) 0%, transparent 70%)',
        pointerEvents: 'none',
    },
    card: {
        background: 'rgba(255,255,255,0.97)', backdropFilter: 'blur(20px)',
        borderRadius: 24, padding: '48px 44px 40px',
        boxShadow: '0 20px 64px rgba(99,102,241,0.13), 0 4px 20px rgba(0,0,0,0.08)',
        width: '100%', maxWidth: 440, position: 'relative', zIndex: 1,
        border: '1px solid rgba(99,102,241,0.15)',
    },
    brandHeader: { textAlign: 'center', marginBottom: 24 },
    logoRing: {
        width: 72, height: 72, borderRadius: '50%',
        background: 'linear-gradient(135deg, #e6f0fa, #c3dcf5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        margin: '0 auto 14px', boxShadow: '0 8px 24px rgba(99,102,241,0.28)',
    },
    logoInner: {
        width: 50, height: 50, borderRadius: '50%',
        background: 'linear-gradient(135deg, #00B2A9, #16539C)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
    },
    brandBadge: {
        display: 'inline-block', padding: '3px 12px', borderRadius: 20,
        background: 'linear-gradient(135deg, #e6f0fa, #c3dcf5)',
        color: '#0D3363', fontSize: 10, fontWeight: 800, letterSpacing: '0.1em',
        border: '1px solid #a5b4fc',
    },
    form: { display: 'flex', flexDirection: 'column', gap: 20 },
    errorBox: {
        background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 12,
        padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10,
        color: '#dc2626', fontSize: 13, fontWeight: 600,
    },
    fieldGroup: { display: 'flex', flexDirection: 'column', gap: 7 },
    label: { fontSize: 12, fontWeight: 700, color: '#374151', letterSpacing: '0.04em', textTransform: 'uppercase' },
    inputWrap: { position: 'relative', display: 'flex', alignItems: 'center' },
    inputIcon: { position: 'absolute', left: 14, display: 'flex', alignItems: 'center', pointerEvents: 'none' },
    input: {
        width: '100%', padding: '13px 16px 13px 44px',
        border: '1.5px solid #e5e7eb', borderRadius: 12,
        fontSize: 14, fontFamily: 'inherit', color: '#1f2937',
        background: '#f9fafb', outline: 'none', boxSizing: 'border-box',
        transition: 'border-color 0.2s, box-shadow 0.2s',
    },
    eyeBtn: {
        position: 'absolute', right: 12, background: 'none', border: 'none',
        cursor: 'pointer', display: 'flex', alignItems: 'center', padding: 4,
    },
    submitBtn: {
        background: 'linear-gradient(135deg, #00B2A9, #16539C)',
        color: 'white', border: 'none', borderRadius: 12, padding: '14px 24px',
        fontSize: 15, fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s',
        boxShadow: '0 4px 16px rgba(99,102,241,0.38)',
        fontFamily: 'inherit', width: '100%',
    },
    spinner: {
        width: 16, height: 16, border: '2px solid rgba(255,255,255,0.3)',
        borderTop: '2px solid white', borderRadius: '50%',
        display: 'inline-block', animation: 'spin 0.8s linear infinite',
    },
};
